<template>
  <div class="page-container">
    <div class="page-header">
      <h2>核心股东控制关系</h2>
      <p>管理核心股东与普通股东的控制关系</p>
    </div>

    <!-- 操作区域 -->
    <div class="action-section">
      <el-button type="primary" @click="handleAdd">
        <el-icon><Plus /></el-icon>
        添加控制关系
      </el-button>
      <el-button type="danger" @click="handleDeleteBatch" :disabled="selectedRows.length === 0">
        批量删除
      </el-button>
      <el-button @click="fetchData">
        <el-icon><Refresh /></el-icon>
        刷新
      </el-button>
    </div>

    <!-- 筛选条件区域 -->
    <div class="filter-section">
      <el-form inline :model="filterForm">
        <el-form-item label="核心股东">
          <HolderSuggestInput v-model="filterForm.core_keyword" type="core" value-key="label" placeholder="汉字/拼音/首字母" style="width: 200px" />
        </el-form-item>
        <el-form-item label="普通股东">
          <HolderSuggestInput v-model="filterForm.raw_keyword" type="raw" value-key="label" placeholder="汉字/拼音/首字母" style="width: 200px" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">
            <el-icon><Search /></el-icon>
            查询
          </el-button>
          <el-button @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 说明信息 -->
    <div class="info-section">
      <div class="info-title">控制关系说明</div>
      <div class="info-content">
        <div class="info-item">
          <el-tag type="danger">账户控制</el-tag>
          <span>自身账户+马甲+完全控制账户交易</span>
        </div>
        <div class="info-item">
          <el-tag type="warning">一致行动</el-tag>
          <span>交易可控+投票一致</span>
        </div>
        <div class="info-item">
          <el-tag class="tag-indirect">间接控制</el-tag>
          <span>交易不可控+投票一致</span>
        </div>
        <div class="info-item">
          <el-tag type="info">仅关联</el-tag>
          <span>交易和投票都不可控，线下去游说争取</span>
        </div>
      </div>
    </div>

    <!-- 数据表格区域 -->
    <div class="table-section">
      <el-table 
        :data="tableData" 
        border 
        stripe 
        style="width: 100%" 
        @selection-change="handleSelectionChange"
        v-loading="loading"
      >
        <el-table-column type="selection" width="55" />
        <el-table-column prop="core_name" label="核心股东" min-width="150">
          <template #default="{ row }">
            <a href="javascript:void(0)" class="holder-link" @click.stop="openHolderDetail(row.core_name, '', '')">{{ row.core_name }}</a>
          </template>
        </el-table-column>
        <el-table-column prop="raw_name" label="普通股东" min-width="150">
          <template #default="{ row }">
            <a href="javascript:void(0)" class="holder-link" @click.stop="openHolderDetail(row.raw_name || row.raw_shareholder_id, row.core_name, row.securities_account_no || row.account_code || row.raw_shareholder_id)">{{ row.raw_name || row.raw_shareholder_id }}</a>
          </template>
        </el-table-column>
        <el-table-column prop="relation_type" label="控制类型" width="100">
          <template #default="{ row }">
            <el-tag :type="getRelationTypeTag(row.relation_type)" :class="row.relation_type === 'indirect' ? 'tag-indirect' : ''">
              {{ getRelationTypeText(row.relation_type) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="source" label="数据来源" width="90">
          <template #default="{ row }">
            <el-tag v-if="row.source === 'manual'" type="success">人工</el-tag>
            <el-tag v-else type="warning">智能</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="evidence_desc" label="证据描述" min-width="120">
          <template #default="{ row }">
            <span v-if="row.source === 'manual'">人工确认</span>
            <span v-else>{{ row.evidence_desc }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="create_time" label="创建时间" width="160" />
        <el-table-column label="操作" width="150">
          <template #default="{ row }">
            <el-button size="small" type="primary" @click="handleEdit(row)">编辑</el-button>
            <el-button size="small" type="danger" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
        />
      </div>
    </div>

    <!-- 添加/编辑弹窗 -->
    <el-dialog 
      v-model="dialogVisible" 
      :title="dialogTitle" 
      width="600px" 
      @close="resetForm"
      :close-on-click-modal="false"
    >
      <el-form :model="form" :rules="rules" ref="formRef" label-width="120px">
        <el-form-item label="普通股东" prop="raw_shareholder_id">
          <HolderSuggestInput
            v-model="form.raw_shareholder_id"
            type="raw"
            placeholder="汉字/拼音/首字母"
            style="width: 100%"
            @select="(item) => { form.raw_name = item.label || item.name || '' }"
          />
        </el-form-item>

        <el-form-item label="核心股东" prop="core_shareholder_id">
          <el-select
            v-model="form.core_shareholder_id"
            placeholder="请选择核心股东"
            filterable
            style="width: 100%"
            :loading="coreShareholdersLoading"
            @change="(id) => { const c = coreShareholders.find(x => x.id === id); form.core_name = c ? c.name : '' }"
          >
            <el-option
              v-for="c in coreShareholders"
              :key="c.id"
              :label="c.name"
              :value="c.id"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="控制类型" prop="relation_type">
          <el-select v-model="form.relation_type" placeholder="请选择控制类型" style="width: 100%">
            <el-option label="账户控制" value="direct" />
            <el-option label="一致行动" value="consistent" />
            <el-option label="间接控制" value="indirect" />
            <el-option label="仅关联" value="associate" />
          </el-select>
        </el-form-item>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit" :loading="submitLoading">确定</el-button>
        </div>
      </template>
      </el-dialog>

    <ShareholderDetailModal
      :key="(holderDetailName || '') + '|' + (holderDetailRawShareholderId || '')"
      v-model="holderDetailVisible"
      :holder-name="holderDetailName"
      :core-name="holderDetailCoreName"
      :link-type="holderDetailLinkType"
      :raw-shareholder-id="holderDetailRawShareholderId"
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Refresh, Search } from '@element-plus/icons-vue'
import axios from 'axios'
import HolderSuggestInput from '@/components/HolderSuggestInput.vue'
import ShareholderDetailModal from '@/components/ShareholderDetailModal.vue'

const holderDetailVisible = ref(false)
const holderDetailName = ref('')
const holderDetailCoreName = ref('')
const holderDetailLinkType = ref(null)
const holderDetailRawShareholderId = ref('')

function openHolderDetail(name, coreName = '', rawShareholderId = '') {
  holderDetailName.value = name || ''
  holderDetailCoreName.value = coreName || ''
  holderDetailLinkType.value = null
  holderDetailRawShareholderId.value = rawShareholderId || ''
  holderDetailVisible.value = true
}

// 表格数据
const tableData = ref([])
const loading = ref(false)
const submitLoading = ref(false)

// 选中的行
const selectedRows = ref([])


// 分页配置
const pagination = reactive({
  currentPage: 1,
  pageSize: 10,
  total: 0
})

// 筛选条件
const filterForm = reactive({
  core_keyword: '',
  raw_keyword: '',
})

// 弹窗相关
const dialogVisible = ref(false)
const formRef = ref(null)
const isEdit = ref(false)

const form = reactive({
  relation_id: '',
  raw_shareholder_id: '',
  raw_name: '',
  core_shareholder_id: '',
  core_name: '',
  relation_type: '',
  status: 'active'
})

const coreShareholders = ref([])
const coreShareholdersLoading = ref(false)

async function fetchCoreShareholders() {
  coreShareholdersLoading.value = true
  try {
    const res = await axios.get('/api/penetration/core-shareholders/')
    if (res.data?.status === 'ok' && res.data?.data) {
      coreShareholders.value = res.data.data.map(c => ({ id: c.core_id, name: c.name }))
    }
  } catch (e) {
    coreShareholders.value = []
  } finally {
    coreShareholdersLoading.value = false
  }
}

// 表单验证规则
const rules = reactive({
  raw_shareholder_id: [
    { required: true, message: '请选择普通股东', trigger: 'change' }
  ],
  core_shareholder_id: [
    { required: true, message: '请选择核心股东', trigger: 'change' }
  ],
  relation_type: [
    { required: true, message: '请选择控制类型', trigger: 'change' }
  ]
})

// 计算弹窗标题
const dialogTitle = computed(() => isEdit.value ? '编辑控制关系' : '添加控制关系')

// 获取控制类型显示文本
const getRelationTypeText = (type) => {
  const map = {
    'direct': '账户控制',
    'consistent': '一致行动',
    'indirect': '间接控制',
    'associate': '仅关联'
  }
  return map[type] || type
}

// 获取控制类型标签类型
const getRelationTypeTag = (type) => {
  const map = {
    'direct': 'danger',
    'consistent': 'warning',
    'indirect': '',
    'associate': 'info'
  }
  return map[type] || 'info'
}

// 获取数据
const fetchData = async () => {
  loading.value = true
  try {
    const params = {
      core_keyword: filterForm.core_keyword,
      raw_keyword: filterForm.raw_keyword,
      status: 'active',
    }
    const response = await axios.get('/api/core_relation/', { 
      params,
      withCredentials: true 
    })
    if (response.data.status === 'ok') {
      tableData.value = response.data.data || []
      pagination.total = response.data.total || 0
    } else {
      ElMessage.error(response.data.message || '获取数据失败')
    }
  } catch (error) {
    console.error('获取数据失败:', error)
    ElMessage.error('获取数据失败')
  } finally {
    loading.value = false
  }
}

// 重置表单
const resetForm = () => {
  form.relation_id = ''
  form.raw_shareholder_id = ''
  form.raw_name = ''
  form.core_shareholder_id = ''
  form.core_name = ''
  form.relation_type = ''
  form.status = 'active'
}

// 添加控制关系
const handleAdd = async () => {
  isEdit.value = false
  resetForm()
  await fetchCoreShareholders()
  if (coreShareholders.value.length) {
    form.core_shareholder_id = coreShareholders.value[0].id
    form.core_name = coreShareholders.value[0].name
  }
  dialogVisible.value = true
}

// 编辑控制关系
const handleEdit = async (row) => {
  isEdit.value = true
  form.relation_id = row.relation_id
  form.raw_shareholder_id = row.raw_shareholder_id
  form.raw_name = row.raw_name
  form.relation_type = row.relation_type
  form.status = 'active'
  await fetchCoreShareholders()
  const existingCore = coreShareholders.value.find(c => c.id === row.core_shareholder_id)
  form.core_shareholder_id = row.core_shareholder_id
  form.core_name = existingCore ? existingCore.name : (row.core_name || '')
  dialogVisible.value = true
}

// 删除控制关系
const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(`确定要删除 "${row.core_name}" -> "${row.raw_name || row.raw_shareholder_id}" 的控制关系吗？`, '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    const response = await axios.delete(`/api/core_relation/delete/${row.relation_id}/`, { withCredentials: true })
    if (response.data.status === 'ok') {
      ElMessage.success('删除成功')
      fetchData()
    } else {
      ElMessage.error(response.data.message|| '删除失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除失败:', error)
      ElMessage.error('删除失败')
    }
  }
}

// 批量删除
const handleDeleteBatch = async () => {
  if (selectedRows.value.length === 0) return
  
  try {
    await ElMessageBox.confirm(`确定要删除选中的 ${selectedRows.value.length} 条控制关系吗？`, '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    const relationIds = selectedRows.value.map(row => row.relation_id)
    const response = await axios.post('/api/core_relation/batch_delete/', { relation_ids: relationIds }, { withCredentials: true })
    if (response.data.status === 'ok') {
      ElMessage.success('批量删除成功')
      fetchData()
    } else {
      ElMessage.error(response.data.message || '删除失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('批量删除失败:', error)
      ElMessage.error('批量删除失败')
    }
  }
}

// 提交表单
const handleSubmit = async () => {
  formRef.value.validate(async (valid) => {
    if (valid) {
      submitLoading.value = true
      try {
        let response
        if (isEdit.value) {
          response = await axios.put(`/api/core_relation/update/${form.relation_id}/`, form, { withCredentials: true })
        } else {
          response = await axios.post('/api/core_relation/create/', form, { withCredentials: true })
        }
        
        if (response.data.status === 'ok') {
          ElMessage.success(isEdit.value ? '编辑成功' : '添加成功')
          dialogVisible.value = false
          fetchData()
        } else {
          ElMessage.error(response.data.message || '操作失败')
        }
      } catch (error) {
        console.error('操作失败:', error)
        ElMessage.error('操作失败')
      } finally {
        submitLoading.value = false
      }
    } else {
      ElMessage.error('请填写完整信息')
    }
  })
}

// 选择行变化
const handleSelectionChange = (val) => {
  selectedRows.value = val
}

// 查询
const handleSearch = () => {
  pagination.currentPage = 1
  fetchData()
}

// 重置
const handleReset = () => {
  filterForm.core_keyword = ''
  filterForm.raw_keyword = ''
  pagination.currentPage = 1
  fetchData()
}

// 分页相关方法
const handleSizeChange = (val) => {
  pagination.pageSize = val
  fetchData()
}
const handleCurrentChange = (val) => {
  pagination.currentPage = val
  fetchData()
}

// 页面加载时获取数据
onMounted(() => {
  fetchData()
})
</script>

<style scoped>
.page-container {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0 0 8px 0;
  color: #303133;
}

.page-header p {
  margin: 0;
  color: #606266;
}

.action-section {
  margin-bottom: 15px;
}

.filter-section {
  background-color: #fff;
  padding: 15px 20px;
  border-radius: 4px;
  margin-bottom: 15px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.table-section {
  background-color: #fff;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.dialog-footer {
  text-align: right;
}

.shareholder-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.hold-qty {
  color: #909399;
  font-size: 12px;
}

.info-section {
  background-color: #f5f7fa;
  padding: 15px 20px;
  border-radius: 4px;
  margin-bottom: 15px;
  border-left: 4px solid #909399;
}

.info-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 10px;
}

.info-content {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-item span {
  color: #606266;
  font-size: 13px;
}

.holder-link {
  color: #409eff;
  text-decoration: none;
  cursor: pointer;
}
.holder-link:hover {
  text-decoration: underline;
}

.tag-indirect,
.el-tag[type=""] {
  background-color: #E6F7FF !important;
  border-color: #91D5FF !important;
  color: #1890FF !important;
}
</style>
